import { kMusicalKeys, type MusicalKey } from "@/shared/musical-key.api";

export const kDiatonicModes = [
    "lydian",
    "ionian",
    "mixolydian",
    "dorian",
    "aeolian",
    "phrygian",
    "locrian",
] as const;

export type DiatonicMode = (typeof kDiatonicModes)[number];

// Human-readable labels. "ionian" and "aeolian" surface as "Major" and "Minor"
// since those are the names users will look for in a dropdown.
export const kDiatonicModeLabels: Record<DiatonicMode, string> = {
    ionian: "Major",
    aeolian: "Minor",
    dorian: "Dorian",
    phrygian: "Phrygian",
    lydian: "Lydian",
    mixolydian: "Mixolydian",
    locrian: "Locrian",
};

export type ScaleProfile = readonly [
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
    number,
];

// Pitch-class profiles for each diatonic mode rooted at C, generated from
// audio_science_genai/features/chroma_scale_builder.py (ChromaScaleBuilder,
// profile_type="krumhansl"). Each profile has exactly 7 non-zero entries at
// the scale-degree positions of that mode. Index 0 = C, 1 = C#, ...
export const kScaleProfilesAtC: Record<DiatonicMode, ScaleProfile> = {
    lydian: [
        1.0, 0.0, 0.545348, 0.0, 0.636378, 0.0, 0.672905, 0.839632, 0.0, 0.55892, 0.0, 0.428842,
    ],
    ionian: [
        1.0, 0.0, 0.548032, 0.0, 0.689764, 0.644095, 0.0, 0.817323, 0.0, 0.576378, 0.0, 0.453543,
    ],
    mixolydian: [
        1.0, 0.0, 0.550715, 0.0, 0.74315, 0.615284, 0.0, 0.795014, 0.0, 0.593836, 0.478244, 0.0,
    ],
    dorian: [
        1.0, 0.0, 0.553399, 0.796535, 0.0, 0.586473, 0.0, 0.772704, 0.0, 0.611294, 0.502945, 0.0,
    ],
    aeolian: [
        1.0, 0.0, 0.556082, 0.849921, 0.0, 0.557662, 0.0, 0.750395, 0.628752, 0.0, 0.527646, 0.0,
    ],
    phrygian: [
        1.0, 0.558766, 0.0, 0.903307, 0.0, 0.528851, 0.0, 0.728086, 0.64621, 0.0, 0.552347, 0.0,
    ],
    locrian: [
        1.0, 0.561449, 0.0, 0.956693, 0.0, 0.50004, 0.705776, 0.0, 0.663668, 0.0, 0.577048, 0.0,
    ],
};

const kNoteToIdx: Record<Exclude<MusicalKey, "">, number> = {
    c: 0,
    "c#": 1,
    d: 2,
    "d#": 3,
    e: 4,
    f: 5,
    "f#": 6,
    g: 7,
    "g#": 8,
    a: 9,
    "a#": 10,
    b: 11,
};

export const getScaleProfile = (key: Exclude<MusicalKey, "">, mode: DiatonicMode): ScaleProfile => {
    const base = kScaleProfilesAtC[mode];
    const shift = kNoteToIdx[key];
    if (shift === 0) {
        return base;
    }
    const rotated: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 12; i++) {
        rotated[(i + shift) % 12] = base[i];
    }
    return rotated as unknown as ScaleProfile;
};

export const kPitchedMusicalKeys = kMusicalKeys.filter(
    (k): k is Exclude<MusicalKey, ""> => k !== "",
);
